import type { App } from "vue";
import axios, { type AxiosResponse, type ResponseType } from "axios";
import { useRepo } from "pinia-orm";
import Account from "@/models/Account";
import User from "@/models/User";
import Group from "@/models/Group";
import { PublicClientApplication } from "@azure/msal-browser";
import { formatDateOnly } from "@/utils/dateUtils";
import type { AuthenticationResult, Configuration } from "@azure/msal-browser";

const DYNAMICS_API_ENDPOINT = import.meta.env.VITE_DYN_API_ENDPOINT;
const DYNAMICS_BASE = import.meta.env.VITE_DYN_API_BASE_URL;
export const DATAVERSE_SCOPES = [`${DYNAMICS_BASE}/.default`];


const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
    authority: import.meta.env.VITE_MSAL_AUTHORITY,
    redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
    postLogoutRedirectUri: import.meta.env.VITE_MSAL_POST_LOGOUT_REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

let isMsalInitialized = false;
const loginInProgress = ref(false);

export const initializeMsal = async () => {
  if (!isMsalInitialized) {
    await msalInstance.initialize();
    isMsalInitialized = true;
  }
};

export const login = async () => {
  try {
    // Prevent multiple login attempts
    if (loginInProgress.value) {
      console.warn('Login already in progress, skipping');
      return null;
    }
    
    loginInProgress.value = true;
    await initializeMsal();

    // Handle any pending redirects (clears MSAL internal state)
    await msalInstance.handleRedirectPromise();

    // Check if there is an active account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      const token = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read.All"],
        account,
      });
      if (token) {
        const response = await callMsGraph(token.accessToken);
        const currentUser = response.data;
        currentUser.graphPhoto = await fetchProfilePhoto(
          currentUser.localAccountId,
        );
        useRepo(Account).fresh(currentUser);
      }
      loginInProgress.value = false;
      return account;
    }

    // If no active account, proceed with login
    const response: AuthenticationResult = await msalInstance.loginPopup({
      scopes: ["User.Read"],
    });

    if (response.account) {
      // console.log("Login successful:", response.account);
      const account = response.account;
      const newAccount = new Account(account);
      newAccount.graphPhoto = await fetchProfilePhoto(account.localAccountId);
      useRepo(Account).fresh(newAccount);
    }
    loginInProgress.value = false;
    return response.account ?? null;
  } catch (error: any) {
    console.error("Login failed:", error);
    loginInProgress.value = false;
    
    // If interaction_in_progress error, clear localStorage and retry once
    if (error?.errorCode === 'interaction_in_progress') {
      console.warn('Clearing MSAL cache due to interaction_in_progress error');
      // Clear MSAL cache entries that track interaction state
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('msal.') && key.includes('.interaction.')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Don't retry immediately - let user click again
      return null;
    }
    
    return null;
  }
};

const clearAllCachesOnLogout = async () => {
  try {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );
    }
  } catch (error) {
    console.error("Error clearing caches on logout:", error);
  }
};

export const logout = async () => {
  await initializeMsal();

  // Clear all cached data before logout
  await clearAllCachesOnLogout();

  // Clear complete users flag
  localStorage.removeItem("vicoma_complete_users_loaded");

  await msalInstance.logoutPopup();
};

export const getAccessToken = async (
  scopes: string[] = ["User.Read.All"], // Default to "User.Read" if no scope is provided
): Promise<string | null> => {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    console.error("Failed to acquire token with scope:", error);

    // Fallback to interactive login if silent token acquisition fails
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes,
      });
      return response.accessToken;
    } catch (popupError) {
      console.error("Failed to acquire token interactively:", popupError);
      return null;
    }
  }
};

export const getCurrentAccount = async () => {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

export const fetchMsdynUserByAzureUserId = async (userId: string) => {
  await initializeMsal();
  const accessToken = await getAccessToken(DATAVERSE_SCOPES);
  if (!accessToken) return;

  const customHeaders = {
    Prefer: `odata.include-annotations="OData.Community.Display.V1.FormattedValue"`,
  };

  const params = {
    $select: "fullname, systemuserid",
    $filter: `azureactivedirectoryobjectid eq '${userId}'`,
  };
  const queryParams = new URLSearchParams(params as any);
  const response = await callDynamics(
    accessToken,
    "/systemusers",
    customHeaders,
    queryParams,
  );
  const msdynUser = response.data.value[0];
  return msdynUser;
};

// Register plugin
export const installMsal = async (app: App) => {
  await initializeMsal();
  app.config.globalProperties.$msal = msalInstance;
};

export const callMsGraph = async (
  accessToken: string,
  path: string = "/me",
  params: URLSearchParams | undefined = undefined,
  responseType: ResponseType = "json",
) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    ConsistencyLevel: "eventual",
  };

  try {
    return await axios.get(`https://graph.microsoft.com/v1.0${path}`, {
      headers,
      params,
      responseType: responseType,
    });
  } catch (error: any) {
    // Don't log 404 errors for expected endpoints (they're normal)
    if (error?.response?.status === 404) {
      // 404 can happen for:
      // - Photo endpoints (user has no photo)
      // - User endpoints (user deleted/not found)
      // - Group membership checks (user not in group)
      // These are expected scenarios, just throw without logging
      throw error;
    }

    console.error("Error in callMsGraph:", error);
    throw error;
  }
};

export const callDynamics = async (
  accessToken: string,
  apiPath: string,
  customHeaders: Record<string, string> = {},
  params: URLSearchParams | undefined = undefined,
  responseType: ResponseType = "json",
): Promise<any> => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    ...customHeaders, // Merge custom headers
  };

  return await axios.get(`${DYNAMICS_BASE}${DYNAMICS_API_ENDPOINT}${apiPath}`, {
    headers,
    params,
    responseType: responseType,
  });
};

export const fetchLoggedInMsdynUser = async (): Promise<any> => {
  await initializeMsal();
  const accessToken = await getAccessToken(DATAVERSE_SCOPES);
  if (!accessToken) return;

  const response = await callDynamics(accessToken, "/WhoAmI");
  const loggedInMsdynUser = response.data;
  return loggedInMsdynUser;
};

export const fetchBookableResourceByMsdynUserId = async (
  msdynUserId: string,
): Promise<any> => {
  await initializeMsal();
  const accessToken = await getAccessToken(DATAVERSE_SCOPES);
  if (!accessToken) return;

  const customHeaders = {
    Prefer: `odata.include-annotations="OData.Community.Display.V1.FormattedValue"`,
  };

  const params = {
    $select:
      "bookableresourceid,name,_userid_value, pyl_contracthours, msdyn_workertype",
    $filter: `_userid_value eq '${msdynUserId}'`,
  };
  const queryParams = new URLSearchParams(params as any);
  const response = await callDynamics(
    accessToken,
    "/bookableresources",
    customHeaders,
    queryParams,
  );
  const bookableResource = response.data.value;
  return bookableResource;
};

// Haalt per Azure AD user-id de indiensttredingsdatum (vic_validfrom) op voor
// medewerkers die na cutoffDate zijn gestart, via bookableresource -> systemuser.
export const fetchRecentHireDates = async (
  cutoffDate: string, // YYYY-MM-DD
): Promise<Record<string, string>> => {
  await initializeMsal();
  const accessToken = await getAccessToken(DATAVERSE_SCOPES);
  if (!accessToken) return {};

  const resourceParams = {
    $select: "_userid_value,vic_validfrom",
    $filter: `vic_validfrom ge ${cutoffDate}`,
  };
  const resourceResponse = await callDynamics(
    accessToken,
    "/bookableresources",
    {},
    new URLSearchParams(resourceParams as any),
  );
  const resources = resourceResponse.data.value as Array<{
    _userid_value: string;
    vic_validfrom: string;
  }>;
  if (resources.length === 0) return {};

  const filter = resources
    .map((resource) => `systemuserid eq '${resource._userid_value}'`)
    .join(" or ");
  const userParams = {
    $select: "systemuserid,azureactivedirectoryobjectid",
    $filter: filter,
  };
  const userResponse = await callDynamics(
    accessToken,
    "/systemusers",
    {},
    new URLSearchParams(userParams as any),
  );
  const systemUsers = userResponse.data.value as Array<{
    systemuserid: string;
    azureactivedirectoryobjectid: string;
  }>;

  const hireDatesByUserId: Record<string, string> = {};
  resources.forEach((resource) => {
    const systemUser = systemUsers.find(
      (su) => su.systemuserid === resource._userid_value,
    );
    if (systemUser?.azureactivedirectoryobjectid) {
      hireDatesByUserId[systemUser.azureactivedirectoryobjectid] =
        resource.vic_validfrom;
    }
  });
  return hireDatesByUserId;
};

export const fetchTimeEntriesByBookableResourceId = async (
  msdynBookableResourceId: string,
  startDate: Date,
  endDate: Date,
  selectFields?: string,
  expandFields?: string,
): Promise<any> => {
  // Converteer naar ISO 8601 datetime strings (YYYY-MM-DDTHH:MM:SSZ) voor OData query
  // Dynamics vereist het volledige datetime-formaat voor filters op Edm.Date.
  // Let op: toISOString() verschuift lokaal middernacht naar de vorige dag in UTC.
  // Dat is bewust: Dynamics registreert urenregels afwijkend rond middernacht en
  // deze verschuiving compenseert dat. Niet "repareren" naar een lokale datum.
  const startDateStr: string = startDate.toISOString().split('T')[0] + 'T00:00:00Z';
  const endDateStr: string = endDate.toISOString().split('T')[0] + 'T23:59:59Z';

  await initializeMsal();
  const accessToken = await getAccessToken(DATAVERSE_SCOPES);
  if (!accessToken) return;

  const customHeaders = {
    Prefer: `odata.include-annotations="OData.Community.Display.V1.FormattedValue"`,
  };

  let filterConditions = `_msdyn_bookableresource_value eq ${msdynBookableResourceId}`;
  filterConditions += ` and msdyn_datetimezoneindependent ge ${startDateStr} and msdyn_datetimezoneindependent le ${endDateStr}`;

  // Gebruik de meegegeven select of de default
  const defaultSelect = "msdyn_date, msdyn_datetimezoneindependent, msdyn_duration, vic_projectnumber,_msdyn_projecttask_value,msdyn_entrystatus, msdyn_description";
  const defaultExpand = "msdyn_project($select=msdyn_subject,vic_projectnumber,_msdyn_customer_value,pyl_internal,msdyn_effort,statecode,vic_jobpostinggroup;$expand=msdyn_customer($select=name,websiteurl,telephone1,address1_composite,_vic_segment_value,_vic_lineofbusinessid_value)),msdyn_bookableresource($select=name,pyl_contracthours,_userid_value),msdyn_projectTask($select=msdyn_subject,_vic_hourcodeid_value,_msdyn_parenttask_value;$expand=vic_hourcodeid($select=vic_hourcode), vic_hourgroupid($select=vic_name))";

  const params = {
    $select: selectFields || defaultSelect,
    $expand: expandFields || defaultExpand,
    $filter: filterConditions,
    $orderby: "msdyn_date asc",
  };
  const queryParams = new URLSearchParams(params as any);
  const response = await callDynamics(
    accessToken,
    "/msdyn_timeentries",
    customHeaders,
    queryParams,
  );
  const timeEntries = response.data.value;
  return timeEntries;
};

export const fetchAllUsers = async (
  selectProperties: string[] = [
    "id",
    "displayName",
    "mail",
    "userPrincipalName",
    "givenName",
    "surname",
    "officeLocation",
    "country",
    "jobTitle",
    "mobilePhone",
    "department",
    "businessPhones",
    "faxNumber",
    "employeeId",
    "department",
    "accountEnabled",
  ],
): Promise<void> => {
  await initializeMsal();
  const accessToken = await getAccessToken();

  const idFilter = [
    "b03bf1f4-1385-4956-af70-f00439b4efe7",
    "ec243721-fe1a-4464-b4e2-5b3c76be3502",
    "b2cdaf9b-5440-4e8d-a06b-de2d6e1b2f97",
    "d8cf552a-0c5e-4e4f-ad1e-5341d745a649",
    "1270b7ee-b1f1-4985-81ab-01ddf5917b77",
    "9cf1cefb-7cd7-4602-a62f-37137540f647",
    "9042ada0-9759-429d-b325-3170e0d91e22",
    "546681d3-9f11-467b-84bf-24069f5a7ef7",
  ];

  if (!accessToken) return;

  try {
    const idFilterString = idFilter.map((id) => `id ne '${id}'`).join(" and ");

    const params = {
      $select: selectProperties.join(","),
      $top: 500,
      $count: true,
      $orderby: "displayName",
      $filter: `jobTitle ne null and accountEnabled eq true and not endsWith(mail, 'onmicrosoft.com') and givenName ne 'Ztw' and ${idFilterString}`,
    };

    const queryParams = new URLSearchParams(params as any);
    const response = await callMsGraph(accessToken, "/users", queryParams);

    const allUsers = response ? response.data.value : [];
    useRepo(User).save(allUsers);

    // Skills worden geladen via backend (index.vue -> backendApiService -> /api/user-skills/batch)
    // Niet hier direct via Graph API aanroepen - dat veroorzaakte hanging requests zonder timeout
  } catch (error) {
    console.error("Failed to fetch users:", error);
  }
};

export const fetchUsersSkillsBatch = async (
  userIds: string[],
): Promise<void> => {
  if (userIds.length === 0) return;

  const accessToken = await getAccessToken();
  if (!accessToken) return;

  const batchSize = 20;
  const concurrentBatches = 10;
  const userRepo = useRepo(User);

  for (let i = 0; i < userIds.length; i += batchSize * concurrentBatches) {
    const batchPromises = [];

    for (
      let j = 0;
      j < concurrentBatches && i + j * batchSize < userIds.length;
      j++
    ) {
      const start = i + j * batchSize;
      const batch = userIds.slice(start, start + batchSize);

      const batchPromise = axios
        .post(
          "https://graph.microsoft.com/v1.0/$batch",
          {
            requests: batch.map((userId) => ({
              id: userId,
              method: "GET",
              url: `/users/${userId}?$select=id,skills`,
            })),
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((response) => {
          response.data.responses.forEach((resp: any) => {
            if (resp.status === 200) {
              const userId = resp.id;
              const skills = resp.body.skills || [];

              const existingUser = userRepo.find(userId);
              if (existingUser) {
                userRepo.save({
                  ...existingUser,
                  id: userId,
                  skills: skills,
                });
              } else {
                userRepo.save({
                  id: userId,
                  skills: skills,
                });
              }
            }
          });
        })
        .catch((error) => {
          console.error(
            `Failed to fetch skills batch ${start}-${start + batchSize}:`,
            error,
          );
        });

      batchPromises.push(batchPromise);
    }

    await Promise.all(batchPromises);

    if (i + batchSize * concurrentBatches < userIds.length) {
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  }
};

export const fetchUsersById = async (
  userId: string,
  selectProperties: string[] = [
    "id",
    "displayName",
    "mail",
    "userPrincipalName",
    "givenName",
    "surname",
    "officeLocation",
    "country",
    "jobTitle",
    "mobilePhone",
    "department",
    "businessPhones",
    "faxNumber",
    "employeeId",
    "department",
    "accountEnabled",
  ],
): Promise<any> => {
  await initializeMsal();
  const accessToken = await getAccessToken();
  if (!accessToken) {
    console.warn("No access token available for fetchUsersById");
    return null;
  }

  try {
    const params = {
      $select: selectProperties.join(","),
    };
    const queryParams = new URLSearchParams(params as any);
    const response = await callMsGraph(
      accessToken,
      `/users/${userId}`,
      queryParams,
    );
    if (!response || !response.data) {
      console.warn(`No data returned for user ${userId}`);
      return null;
    }
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      console.warn(`User not found: ${userId}`);
      return null;
    }
    console.error(`Failed to fetch user ${userId}:`, error);
    return null;
  }
};

export const fetchProfilePhoto = async (
  userId: string,
  resolution: string = "240x240",
): Promise<any> => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    console.warn("No access token available for fetchProfilePhoto");
    return null;
  }

  try {
    const path = `/users/${userId}/photos/${resolution}/$value`;
    const response = await callMsGraph(accessToken, path, undefined, "blob");
    if (!response || !response.data) {
      console.warn(`No photo data returned for user ${userId}`);
      return null;
    }
    const photoUrl: string = URL.createObjectURL(response.data);
    return photoUrl;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.warn(
      `Could not fetch photo for user ${userId}:`,
      error?.response?.status || error.message,
    );
    return null;
  }
};

export const fetchFilteredGroups = async (): Promise<void> => {
  const accessToken = await getAccessToken(["Group.Read.All"]);
  if (!accessToken) return;

  const groupPrefixes = [
    "azg-HVL",
    "azg-STO",
    "azg-VEL",
    "azg-GRO",
    "azg-IND",
    "azg-dep-",
  ];

  const groupFilter = groupPrefixes
    .map((prefix) => `startswith(displayName,'${prefix}')`)
    .join(" or ");
  const combinedFilter = `(${groupFilter}) and mailEnabled eq false and securityEnabled eq true`;

  try {
    const params = new URLSearchParams({
      $filter: combinedFilter,
      $select: "id,displayName,description",
      $expand: "members($select=id)",
    });
    const response = await callMsGraph(accessToken, "/groups", params);
    const filteredGroups = await Promise.all(
      response.data.value.map(async (group: any) => {
        let members = group.members;
        if (members.length === 20) {
          members = await fetchAllMembersForGroup(group.id);
        }
        const existingUserIds = members.map((member: any) => member.id);
        return {
          id: group.id,
          displayName: group.displayName,
          description: group.description,
          members: existingUserIds.map((id: any) => {
            return { id: id };
          }),
        };
      }),
    );

    useRepo(Group).save(filteredGroups);
  } catch (error) {
    console.error("Failed to fetch filtered groups:", error);
  }
};

export const isLoginInProgress = () => {
  return loginInProgress.value;
};

export const fetchAllMembersForGroup = async (
  groupId: string,
): Promise<any[]> => {
  const accessToken = await getAccessToken(["Group.Read.All"]);
  if (!accessToken) return [];
  let members: any[] = [];
  let nextLink = `/groups/${groupId}/members?$select=id`;

  while (nextLink) {
    const response = await callMsGraph(accessToken, nextLink);
    members = members.concat(response.data.value);
    nextLink = response.data["@odata.nextLink"] || null;
  }
  return members;
};

// Checks the signed-in user's membership of multiple groups in a single
// Graph call. Returns the subset of `groupIds` the user belongs to
// (transitively — nested group membership is resolved by Graph). Falsy/empty
// ids are ignored, matching the old per-group "unset env var => false"
// behaviour. Uses /me/checkMemberGroups, which only needs the default
// User.Read scope (no Group.Read.All / admin consent required), unlike the
// old per-group /groups/{id}/members/{id} lookup.
export const checkMemberGroups = async (
  groupIds: (string | undefined)[],
): Promise<Set<string>> => {
  const requestedIds = groupIds.filter((id): id is string => Boolean(id));
  if (requestedIds.length === 0) return new Set();

  try {
    const accessToken = await getAccessToken(["User.Read"]);
    if (!accessToken) {
      console.error("Access token not found");
      return new Set();
    }

    const response = await axios.post(
      "https://graph.microsoft.com/v1.0/me/checkMemberGroups",
      { groupIds: requestedIds },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return new Set<string>(response.data.value ?? []);
  } catch (error) {
    console.error("Error checking group memberships:", error);
    return new Set();
  }
};

export const findTeamsByProjectNumber = async (
  projectNumber: string,
): Promise<string | null> => {
  try {
    const accessToken = await getAccessToken([
      "Team.ReadBasic.All",
      "Group.Read.All",
    ]);

    if (!accessToken) {
      console.error("Failed to acquire access token");
      return null;
    }

    const projectNumbers = [projectNumber];
    if (/-\d{2}$/.test(projectNumber)) {
      const baseProjectNumber = projectNumber.replace(/-\d{2}$/, "");
      projectNumbers.push(baseProjectNumber);
      console.log(
        `Project number has suffix. Will try both: ${projectNumber} and ${baseProjectNumber}`,
      );
    }

    for (const projNum of projectNumbers) {
      const teamsParams = new URLSearchParams({
        $filter: `contains(displayName, '${projNum}')`,
        $select: "id,displayName",
      });
      try {
        const teamsResponse = await callMsGraph(
          accessToken,
          "/teams",
          teamsParams,
        );
        if (teamsResponse.data.value && teamsResponse.data.value.length > 0) {
          const team = teamsResponse.data.value[0];
          try {
            const teamDetailsResponse = await callMsGraph(
              accessToken,
              `/teams/${team.id}`,
            );
            if (teamDetailsResponse.data && teamDetailsResponse.data.webUrl) {
              return teamDetailsResponse.data.webUrl;
            } else {
              return `https://teams.microsoft.com/l/team/19:${team.id}@thread.tacv2/conversations?groupId=${team.id}`;
            }
          } catch (detailsError) {
            console.warn(
              `Error getting team details, using constructed URL:`,
              detailsError,
            );
            return `https://teams.microsoft.com/l/team/19:${team.id}@thread.tacv2/conversations?groupId=${team.id}`;
          }
        }
      } catch (teamsError) {
        console.warn(`Error searching teams, will try groups:`, teamsError);
      }

      const groupParams = new URLSearchParams({
        $filter: `startsWith(displayName, '${projNum}')`,
        $select: "id,displayName,mail,resourceProvisioningOptions",
      });
      const groupResponse = await callMsGraph(
        accessToken,
        "/groups",
        groupParams,
      );
      console.log(`Groups search response for ${projNum}:`, groupResponse.data);
      if (groupResponse.data.value && groupResponse.data.value.length > 0) {
        for (const group of groupResponse.data.value) {
          const isTeamsEnabled =
            group.resourceProvisioningOptions &&
            group.resourceProvisioningOptions.includes("Team");
          if (isTeamsEnabled) {
            try {
              const teamResponse = await callMsGraph(
                accessToken,
                `/groups/${group.id}/team`,
              );

              if (teamResponse.data && teamResponse.data.webUrl) {
                return teamResponse.data.webUrl;
              } else {
                return `https://teams.microsoft.com/l/team/19:${group.id}@thread.tacv2/conversations?groupId=${group.id}`;
              }
            } catch (teamError) {
              console.warn(
                `Error getting team for group, using constructed URL:`,
                teamError,
              );
              return `https://teams.microsoft.com/l/team/19:${group.id}@thread.tacv2/conversations?groupId=${group.id}`;
            }
          }
        }
        const group = groupResponse.data.value[0];
        console.log(`Using first matching group: ${group.displayName}`);
        return `https://teams.microsoft.com/l/team/19:${group.id}@thread.tacv2/conversations?groupId=${group.id}`;
      }
    }
    console.log("No teams found after trying all project number variants");
    return null;
  } catch (error) {
    console.error("Error searching for team:", error);
    return null;
  }
};

export const fetchAllUsersSkills = async (): Promise<void> => {
  console.log("Fetching all user skills...");
  const accessToken = await getAccessToken();
  if (!accessToken) return;

  const userRepo = useRepo(User);
  const allUserIds = userRepo.all().map((u) => u.id);

  if (allUserIds.length === 0) {
    console.log("No users to fetch skills for");
    return;
  }

  const batchSize = 20;
  const concurrentBatches = 10;

  for (let i = 0; i < allUserIds.length; i += batchSize * concurrentBatches) {
    const batchPromises = [];

    for (
      let j = 0;
      j < concurrentBatches && i + j * batchSize < allUserIds.length;
      j++
    ) {
      const start = i + j * batchSize;
      const batch = allUserIds.slice(start, start + batchSize);

      const batchPromise = axios
        .post(
          "https://graph.microsoft.com/v1.0/$batch",
          {
            requests: batch.map((userId) => ({
              id: userId,
              method: "GET",
              url: `/users/${userId}?$select=id,skills`,
            })),
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((response) => {
          response.data.responses.forEach((resp: any) => {
            if (resp.status === 200) {
              userRepo.save({
                id: resp.id,
                skills: resp.body.skills || [],
              });
            }
          });
        })
        .catch((error) => {
          console.error(`Failed to fetch skills batch:`, error);
        });

      batchPromises.push(batchPromise);
    }

    await Promise.all(batchPromises);

    if (i + batchSize * concurrentBatches < allUserIds.length) {
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  }
  console.log(`Finished loading skills for ${allUserIds.length} users`);
};

// export const fetchAggregatedTimeEntriesPerBookableResource = async (
//   startDate: Date,
//   endDate: Date,
// ): Promise<
//   Array<{
//     resourceId: string;
//     entryStatus: number;
//     totalHours: number;
//     contractHours: number;
//     userId: string;
//     azureObjectId: string;
//     workerType?: number | string | null;
//     billableHours: number;
//     nonBillableHours: number;
//     hrHours: number;
//     orgUnit?: string | null;
//     userFullname?: string | null;
//     userEmail?: string | null;
//     department?: string | null;
//     officeLocation?: string | null;
//     stateCode?: number | null;
//     statusCode?: number | null;
//     azureDeletedOn?: string | null;
//   }>
// > => {
//   await initializeMsal();
//   const accessToken = await getAccessToken(DATAVERSE_SCOPES);
//   if (!accessToken) return [];

//   // Format dates as YYYY-MM-DD in local timezone (niet UTC om timezone verschuiving te voorkomen)
//   const formatLocalDate = (date: Date): string => {
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
//   };

//   let startDateStr = formatLocalDate(startDate);
//   let endDateStr = formatLocalDate(endDate);

//   // Check if we're querying future/current period (startDate > today)
//   const today = new Date();
//   today.setHours(0, 0, 0, 0);
//   const isFutureOrCurrent = startDate > today;

//   // Voor toekomstige/huidige periodes: filter op actieve users (statecode = 0)
//   // Voor historische periodes: laat ex-medewerkers zien die toen actief waren
//   const stateCodeFilter = isFutureOrCurrent
//     ? '<condition attribute="statecode" operator="eq" value="0" />'
//     : "";

//   // QUERY 1: Aggregate data
//   const fetchXml = `
//     <fetch aggregate="true">
//       <entity name="bookableresource">
//         <attribute name="bookableresourceid" alias="resource_id" groupby="true" />
//         <attribute name="name" alias="resource_name" groupby="true" />
//         <attribute name="pyl_contracthours" alias="contract_hours" groupby="true" />
//         <attribute name="msdyn_workertype" alias="worker_type" groupby="true" />
//         <attribute name="statecode" alias="state_code" groupby="true" />
//         <attribute name="statuscode" alias="status_code" groupby="true" />
//         <link-entity name="vic_department" from="vic_departmentid" to="vic_department" alias="dept" link-type="outer">
//           <attribute name="vic_name" alias="department_name" groupby="true" />
//         </link-entity>
//         <link-entity name="msdyn_organizationalunit" from="msdyn_organizationalunitid" to="msdyn_organizationalunit" alias="orgunit" link-type="inner">
//           <attribute name="msdyn_organizationalunitid" alias="org_unit" groupby="true" />
//           <attribute name="msdyn_name" alias="office_location_name" groupby="true" />
//         </link-entity>
//         <link-entity name="systemuser" from="systemuserid" to="userid" alias="su" link-type="outer">
//           <attribute name="systemuserid" alias="system_user_id" groupby="true"/>
//           <attribute name="azureactivedirectoryobjectid" alias="ad_object_id" groupby="true" />
//           <attribute name="fullname" alias="user_fullname" groupby="true" />
//           <attribute name="internalemailaddress" alias="user_email" groupby="true" />
//         </link-entity>
//         <link-entity name="msdyn_timeentry" from="msdyn_bookableresource" to="bookableresourceid" alias="te" link-type="outer">
//           <attribute name="msdyn_duration" alias="total_duration" aggregate="sum" />
//           <attribute name="msdyn_entrystatus" alias="entry_status" groupby="true" />
//           <link-entity name="msdyn_project" from="msdyn_projectid" to="msdyn_project" alias="proj" link-type="outer">
//             <attribute name="pyl_internal" alias="is_internal" groupby="true" />
//           </link-entity>
//           <link-entity name="msdyn_projecttask" from="msdyn_projecttaskid" to="msdyn_projecttask" alias="task" link-type="outer">
//             <attribute name="msdyn_subject" alias="task_subject" groupby="true" />
//           </link-entity>
//             <filter type="and">
//               <condition attribute="msdyn_datetimezoneindependent" operator="on-or-after" value="${startDateStr}" />
//               <condition attribute="msdyn_datetimezoneindependent" operator="on-or-before" value="${endDateStr}" />

//             </filter>
//         </link-entity>
//         <filter type="and">
//           ${stateCodeFilter}
//           <condition attribute="userid" operator="ne" value="649c8350-3760-ed11-9561-0022489ca8f4" />
//           <condition attribute="userid" operator="ne" value="e66fa9e1-cba7-ec11-9840-000d3adf7e37" />
//           <condition attribute="userid" operator="ne" value="4ad125fc-40b6-ec11-983e-000d3a67070e" />
//           <condition attribute="userid" operator="ne" value="61782c23-dfaa-ec11-b3fe-000d3ab92fb0" />
//           <condition attribute="bookableresourceid" operator="ne" value="152216ff-3ca8-ec11-9840-000d3adf7e37" />
//           <condition attribute="bookableresourceid" operator="ne" value="6414cbcb-f59a-ed11-aad1-000d3a20d805" />
//           <condition attribute="bookableresourceid" operator="ne" value="378814e9-33ee-ed11-8849-000d3a236a84" />
//           <condition attribute="msdyn_organizationalunit" operator="not-null" />

//         </filter>
//       </entity>
//     </fetch>
//   `.trim();

//   try {
//     const params = new URLSearchParams({ fetchXml });
//     const response = await callDynamics(
//       accessToken,
//       "/bookableresources",
//       {},
//       params,
//     );

//     // DEBUG: Log problematic users
//     const problematicUsers = response.data.value.filter(
//       (entry: any) =>
//         entry.resource_name === "(Onbekend)" ||
//         entry.resource_name === null ||
//         entry.ad_object_id === null ||
//         entry.ad_object_id === "",
//     );
//     if (problematicUsers.length > 0) {
//       console.warn(
//         `⚠️ Found ${problematicUsers.length} users with issues:`,
//         problematicUsers.map((u: any) => ({
//           resourceId: u.resource_id,
//           userId: u.system_user_id,
//           resourceName: u.resource_name || "NULL",
//           systemUserName: u.user_fullname || "NULL",
//           azureObjectId: u.ad_object_id || "NULL",
//           email: u.user_email || "NULL",
//           stateCode: u.state_code,
//           statusCode: u.status_code,
//           issue: !u.ad_object_id
//             ? "NO_AZURE_ID"
//             : u.resource_name === "(Onbekend)" || !u.resource_name
//               ? "UNKNOWN_NAME"
//               : "OTHER",
//         })),
//       );
//     }

//     // Identificeer ALLE inactive users voor query 2 (we moeten vic_validuntil + azuredeletedon ophalen)
//     const inactiveUserIds = response.data.value
//       .filter((entry: any) => {
//         if (isFutureOrCurrent) return false; // Geen inactive users in future/current
//         const stateCode =
//           entry.state_code != null ? parseInt(entry.state_code) : 0;
//         return stateCode === 1; // Alleen inactive users
//       })
//       .map((entry: any) => entry.system_user_id)
//       .filter((id: string) => id != null);

//     const uniqueUserIds = [...new Set(inactiveUserIds)];

//     // QUERY 2: Haal vic_validuntil + azuredeletedon op voor inactive users
//     // (bepaal in post-processing of vic_validuntil betrouwbaar is)
//     let userDetailMap: Map<
//       string,
//       { validUntil: string | null; azureDeletedOn: string | null }
//     > = new Map();

//     if (uniqueUserIds.length > 0) {
//       const userIdFilter = uniqueUserIds
//         .map((id) => `systemuserid eq ${id}`)
//         .join(" or ");

//       // Haal user details op via bookableresource + systemuser join
//       const detailFetchXml = `
//         <fetch>
//           <entity name="bookableresource">
//             <attribute name="vic_validuntil" />
//             <link-entity name="systemuser" from="systemuserid" to="userid" alias="su" link-type="inner">
//               <attribute name="systemuserid" />
//               <attribute name="azuredeletedon" />
//             </link-entity>
//             <filter type="or">
//               ${userIdFilter.replace(/systemuserid/g, "userid")}
//             </filter>
//           </entity>
//         </fetch>
//       `.trim();

//       try {
//         const detailParams = new URLSearchParams({ fetchXml: detailFetchXml });
//         const detailResponse = await callDynamics(
//           accessToken,
//           "/bookableresources",
//           {},
//           detailParams,
//         );

//         // Build lookup map: userId -> { validUntil, azureDeletedOn }
//         detailResponse.data.value.forEach((resource: any) => {
//           userDetailMap.set(resource["su.systemuserid"], {
//             validUntil: resource.vic_validuntil || null,
//             azureDeletedOn: resource["su.azuredeletedon"] || null,
//           });
//         });
//       } catch (detailError) {
//         console.warn(
//           "Failed to fetch user details, continuing without:",
//           detailError,
//         );
//       }
//     }

//     // MERGE: Combine aggregate data met user details (vic_validuntil + azuredeletedon)
//     const hoursPerStatus = response.data.value
//       .filter((entry: any) => {
//         // Voor historische periodes: intelligente filtering
//         if (!isFutureOrCurrent) {
//           const stateCode =
//             entry.state_code != null ? parseInt(entry.state_code) : 0;
//           const isInactive = stateCode === 1;

//           if (isInactive) {
//             const userId = entry.system_user_id;
//             const userDetails = userDetailMap.get(userId);

//             if (userDetails) {
//               const validUntil = userDetails.validUntil;
//               const azureDeletedOn = userDetails.azureDeletedOn;

//               // Check of vic_validuntil betrouwbaar is
//               const isValidUntilReliable =
//                 validUntil && new Date(validUntil) < new Date("2154-01-01");

//               if (isValidUntilReliable) {
//                 // vic_validuntil is betrouwbaar, gebruik die
//                 // (vic_validfrom/validuntil filters in query zorgen al voor correcte filtering)
//                 return true;
//               } else if (azureDeletedOn) {
//                 // vic_validuntil onbetrouwbaar (2154 of null), gebruik azuredeletedon
//                 const deletedDate = new Date(azureDeletedOn);
//                 // Exclude als user verwijderd was vóór de periode
//                 if (deletedDate < startDate) {
//                   return false;
//                 }
//               }
//               // Geen betrouwbare data: vertrouw op query filters
//             }
//           }
//         }
//         return true;
//       })
//       .map((entry: any) => {
//         const totalMinutes = parseInt(entry.total_duration) || 0;
//         const isInternal = entry.is_internal === true;
//         const taskSubject = entry.task_subject || "";

//         // HR-taken
//         const isHRTask = taskSubject.startsWith("HR-");
//         const hrMinutes = isHRTask ? totalMinutes : 0;

//         // Billable = externe projecten (pyl_internal = false)
//         const billableMinutes = !isInternal ? totalMinutes : 0;

//         // Non-billable = interne projecten (pyl_internal = true)
//         const nonBillableMinutes = isInternal ? totalMinutes : 0;

//         const userId = entry.system_user_id;
//         const userDetails = userDetailMap.get(userId);
//         const azureDeletedOn = userDetails?.azureDeletedOn || null;

//         return {
//           resourceId: entry.resource_id,
//           entryStatus: parseInt(entry.entry_status) || 0,
//           totalHours: totalMinutes / 60,
//           contractHours: parseFloat(entry.contract_hours) || 0,
//           userId: userId,
//           azureObjectId: entry.ad_object_id,
//           workerType: entry.worker_type,
//           billableHours: billableMinutes / 60,
//           nonBillableHours: nonBillableMinutes / 60,
//           hrHours: hrMinutes / 60,
//           orgUnit: entry.org_unit || null,
//           userFullname: entry.resource_name || null,
//           userEmail: entry.user_email || null,
//           department: entry.department_name || null,
//           officeLocation: entry.office_location_name || null,
//           stateCode:
//             entry.state_code != null ? parseInt(entry.state_code) : null,
//           statusCode:
//             entry.status_code != null ? parseInt(entry.status_code) : null,
//           azureDeletedOn: azureDeletedOn,
//         };
//       });
//       console.log(hoursPerStatus);
//       console.log(`Fetched hours by status for ${hoursPerStatus.length} bookable resources between ${startDateStr} and ${endDateStr}. Future/current period: ${isFutureOrCurrent}. Problematic users with (Onbekend) or missing Azure ID: ${problematicUsers.length}. Inactive users in historical period: ${inactiveUserIds.length}. Unique inactive users: ${uniqueUserIds.length}. User details fetched: ${userDetailMap.size}.`,
//       );
//     return hoursPerStatus;
//   } catch (error) {
//     console.error("Error fetching hours by status:", error);
//     return [];
//   }
// };

// export const fetchAggregatedTimeEntries = async (
//   year: number,
// ): Promise<
//   Array<{
//     resourceId: string;
//     projectSubject: string;
//     isInternal: boolean;
//     userId: string;
//     azureObjectId: string;
//     date: string;
//     weekNumber: number;
//     yearNumber: number;
//     totalHours: number;
//     billableHours: number;
//     nonBillableHours: number;
//     hrHours: number;
//     orgUnit?: string | null;
//     entryStatus: number;
//   }>
// > => {
//   await initializeMsal();
//   const accessToken = await getAccessToken(DATAVERSE_SCOPES);
//   if (!accessToken) return [];

//   // Splits queries per kwartaal om 50K limiet te vermijden
//   const quarters = [
//     { start: new Date(year, 0, 1), end: new Date(year, 3, 0, 23, 59, 59, 999) },    // Q1: jan-mrt
//     { start: new Date(year, 3, 1), end: new Date(year, 6, 0, 23, 59, 59, 999) },    // Q2: apr-jun
//     { start: new Date(year, 6, 1), end: new Date(year, 9, 0, 23, 59, 59, 999) },    // Q3: jul-sep
//     { start: new Date(year, 9, 1), end: new Date(year + 1, 0, 0, 23, 59, 59, 999) }, // Q4: okt-dec
//   ];

//   const allResults: any[] = [];

//   // Fetch elk kwartaal parallel
//   const quarterPromises = quarters.map(async (quarter, index) => {
//     const startDateStr = quarter.start.toISOString().split('T')[0]; // YYYY-MM-DD format
//     const endDateStr = quarter.end.toISOString().split('T')[0];

//     console.log(`Fetching Q${index + 1} ${year}: ${quarter.start.toLocaleDateString()} - ${quarter.end.toLocaleDateString()}`);

//     const baseFetchXml = (pagingCookie: string = '') => `
//     <fetch aggregate="true" ${pagingCookie}>
//       <entity name="msdyn_timeentry">
//         <!-- Aggregatie: som van duration per resource per week per project -->
//         <attribute name="msdyn_duration" alias="total_duration" aggregate="sum" />
//         <attribute name="msdyn_bookableresource" alias="resource_id" groupby="true" />

//         <!-- Groeperen op week en jaar -->
//         <attribute name="msdyn_date" alias="week_number" groupby="true" dategrouping="week" />
//         <attribute name="msdyn_date" alias="year_number" groupby="true" dategrouping="year" />

//         <!-- Date range filter op time entries -->
//         <filter type="and">
//           <condition attribute="msdyn_date" operator="on-or-after" value="${startDateStr}" />
//           <condition attribute="msdyn_date" operator="on-or-before" value="${endDateStr}" />
//           <!-- Filter op Approved (192350002) en Submitted (192350003) entries -->
//           <condition attribute="msdyn_entrystatus" operator="in">
//             <value>192350002</value>
//             <value>192350003</value>
//           </condition>
//         </filter>

//         <!-- Link naar bookable resource ALLEEN voor filters (niet voor groepering) -->
//         <link-entity name="bookableresource" from="bookableresourceid" to="msdyn_bookableresource" alias="br">
//           <filter type="and">
//             <condition attribute="userid" operator="ne" value="649c8350-3760-ed11-9561-0022489ca8f4" />
//             <condition attribute="userid" operator="ne" value="e66fa9e1-cba7-ec11-9840-000d3adf7e37" />
//             <condition attribute="userid" operator="ne" value="4ad125fc-40b6-ec11-983e-000d3ab92fb0" />
//             <condition attribute="userid" operator="ne" value="61782c23-dfaa-ec11-b3fe-000d3ab92fb0" />
//             <condition attribute="bookableresourceid" operator="ne" value="152216ff-3ca8-ec11-9840-000d3adf7e37" />
//             <condition attribute="bookableresourceid" operator="ne" value="6414cbcb-f59a-ed11-aad1-000d3a20d805" />
//             <condition attribute="bookableresourceid" operator="ne" value="378814e9-33ee-ed11-8849-000d3a236a84" />
//           </filter>
//           <!-- Geen uitsluiting meer van Beheer org unit -->
//         </link-entity>

//         <!-- Link naar project voor subject en internal flag (groeperen) -->
//         <link-entity name="msdyn_project" from="msdyn_projectid" to="msdyn_project" alias="proj" link-type="inner">
//           <attribute name="msdyn_subject" alias="project_subject" groupby="true" />
//           <attribute name="pyl_internal" alias="is_internal" groupby="true" />
//         </link-entity>

//         <!-- Link naar project task voor HR-detectie (task subject) -->
//         <link-entity name="msdyn_projecttask" from="msdyn_projecttaskid" to="msdyn_projecttask" alias="task" link-type="outer">
//           <attribute name="msdyn_subject" alias="task_subject" groupby="true" />
//         </link-entity>

//         <!-- Sorteer op resource en week -->
//         <order alias="year_number" />
//         <order alias="week_number" />
//       </entity>
//     </fetch>`.trim();

//     try {
//       const quarterResults: any[] = [];
//       let pagingCookie = '';
//       let pageNumber = 1;

//       // Loop door alle pages tot er geen cookie meer is
//       do {
//         const fetchXml = baseFetchXml(pagingCookie);
//         const params = new URLSearchParams({ fetchXml });
//         const response = await callDynamics(
//           accessToken,
//           "/msdyn_timeentries",
//           {},
//           params,
//         );

//         quarterResults.push(...response.data.value);
//         console.log(`Q${index + 1} ${year} page ${pageNumber}: Fetched ${response.data.value.length} rows (total: ${quarterResults.length})`);

//         // Check voor volgende page
//         const nextPageCookie = response.data['@Microsoft.Dynamics.CRM.fetchxmlpagingcookie'];
//         if (nextPageCookie) {
//           // Decode de cookie en extract page + pagingcookie attributen
//           const decodedCookie = decodeURIComponent(decodeURIComponent(nextPageCookie));
//           const pageMatch = decodedCookie.match(/pagenumber="(\d+)"/);
//           const cookieMatch = decodedCookie.match(/pagingcookie="([^"]+)"/);

//           if (pageMatch && cookieMatch) {
//             const nextPage = parseInt(pageMatch[1]);
//             const cookie = decodeURIComponent(cookieMatch[1]);
//             pagingCookie = `page="${nextPage}" paging-cookie="${encodeURIComponent(cookie)}"`;
//             pageNumber = nextPage;
//           } else {
//             pagingCookie = '';
//           }
//         } else {
//           pagingCookie = '';
//         }
//       } while (pagingCookie);

//       console.log(`Q${index + 1} ${year}: Total ${quarterResults.length} aggregated rows across ${pageNumber} pages`);
//       return quarterResults;
//     } catch (error) {
//       console.error(`Error fetching Q${index + 1} ${year}:`, error);
//       return [];
//     }
//   });

//   // Wacht op alle kwartalen
//   const quarterResults = await Promise.all(quarterPromises);

//   // Combineer alle resultaten
//   quarterResults.forEach(quarterData => {
//     allResults.push(...quarterData);
//   });

//   console.log(`Total aggregated rows for ${year}: ${allResults.length}`);

//   // DEBUG: Log eerste paar entries om Dynamics week-nummers te checken
//   if (allResults.length > 0) {
//     console.warn('🔍 Dynamics week numbering check (eerste 5 entries):',
//       allResults.slice(0, 5).map((entry: any) => ({
//         weekNumber: entry.week_number,
//         yearNumber: entry.year_number,
//         projectSubject: entry.project_subject,
//         totalMinutes: entry.total_duration
//       }))
//     );
//   }

//   // Transformeer aggregated results naar het formaat dat dashboard verwacht
//   const transformedData = allResults.map((entry: any) => {
//       const totalMinutes = parseInt(entry.total_duration) || 0;
//       const isInternal = entry.is_internal === true;
//       const projectSubject = entry.project_subject || "";
//       const taskSubject = entry.task_subject || "";

//       // Construeer een datum voor deze week (eerste dag van de week)
//       const weekNumber = parseInt(entry.week_number) || 1;
//       const yearNumber = parseInt(entry.year_number) || year;

//       // ISO week date: jaar + week nummer
//       // We gebruiken week 1 dag 1 als representatie
//       const date = new Date(yearNumber, 0, 1 + (weekNumber - 1) * 7);

//       // HR-taken detecteren op basis van task subject (bijv. "HR-Verlof", "HR-Ziekte")
//       const isHRTask = taskSubject.startsWith('HR-');
//       const hrMinutes = isHRTask ? totalMinutes : 0;

//       // Bereken billable/non-billable op basis van is_internal flag
//       // Billable = externe projecten (pyl_internal = false), maar niet HR
//       const billableMinutes = !isInternal ? totalMinutes : 0;

//       // Non-billable = interne projecten (pyl_internal = true), maar niet HR
//       const nonBillableMinutes = isInternal ? totalMinutes : 0;

//       return {
//         resourceId: entry.resource_id,
//         projectSubject: projectSubject || "Geen project", // Project naam
//         isInternal: isInternal, // Internal/external flag
//         userId: "", // Niet beschikbaar in aggregatie, dashboard moet ophalen
//         azureObjectId: "", // Niet beschikbaar in aggregatie, dashboard moet ophalen
//         date: date.toISOString(),
//         weekNumber: weekNumber,
//         yearNumber: yearNumber,
//         billableHours: billableMinutes / 60,
//         nonBillableHours: nonBillableMinutes / 60,
//         hrHours: hrMinutes / 60, // HR-taken (verlof, ziekte, etc.)
//         totalHours: totalMinutes / 60, // Totale uren per week per project
//         orgUnit: null, // Niet beschikbaar in aggregatie
//         entryStatus: 0, // Niet beschikbaar in deze aggregatie
//       };
//     });

//   return transformedData;
// };
